import { useState, useEffect } from "react";
import axios from "axios";
import { X, MessageCircle, Brain, Sparkles, TrendingUp, TrendingDown, Minus, Flame, Heart, BookOpen } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MOOD_LABELS = {
  0: "No mood logged",
  1: "Very low", 2: "Low", 3: "Struggling", 4: "Below average",
  5: "Neutral", 6: "Okay", 7: "Good", 8: "Great", 9: "Excellent", 10: "Outstanding"
};

const TREND_CONFIG = {
  improving: { icon: TrendingUp, label: "Improving", color: "text-emerald-500", bg: "bg-emerald-500/10" },
  declining: { icon: TrendingDown, label: "Declining", color: "text-amber-500", bg: "bg-amber-500/10" },
  steady: { icon: Minus, label: "Steady", color: "text-blue-500", bg: "bg-blue-500/10" },
};

export function DailyRecap({ token, onDismiss }) {
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already dismissed today
    const today = new Date().toISOString().split("T")[0];
    const lastDismissed = localStorage.getItem("omni_recap_dismissed");
    if (lastDismissed === today) {
      setDismissed(true);
      setLoading(false);
      return;
    }
    fetchRecap();
  }, []);

  const fetchRecap = async () => {
    try {
      const { data } = await axios.get(`${API}/chat/daily-recap`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data.has_activity) {
        setRecap(data);
      }
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    const today = new Date().toISOString().split("T")[0];
    localStorage.setItem("omni_recap_dismissed", today);
    setDismissed(true);
    onDismiss?.();
  };

  if (loading || dismissed || !recap) return null;

  const trend = TREND_CONFIG[recap.mood_trend] || TREND_CONFIG.steady;
  const TrendIcon = trend.icon;
  const moodLabel = MOOD_LABELS[Math.round(recap.avg_mood)] || "";

  return (
    <div
      data-testid="daily-recap"
      className="mx-2 sm:mx-4 mt-2 rounded-2xl border border-border/50 bg-gradient-to-br from-card via-card to-primary/[0.03] shadow-sm overflow-hidden animate-in slide-in-from-top-2 duration-300"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-1">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-xl bg-primary/10 flex items-center justify-center">
            <BookOpen className="w-3.5 h-3.5 text-primary" />
          </div>
          <div>
            <h4 className="text-xs font-bold text-foreground">Your Day So Far</h4>
            <p className="text-[10px] text-muted-foreground">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</p>
          </div>
        </div>
        <button
          data-testid="recap-dismiss"
          onClick={handleDismiss}
          className="p-1 rounded-full hover:bg-muted/60 transition-colors"
        >
          <X className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      </div>

      {/* AI Highlight */}
      {recap.highlight && (
        <p data-testid="recap-highlight" className="px-4 py-2 text-sm text-foreground/90 leading-relaxed italic">
          &ldquo;{recap.highlight}&rdquo;
        </p>
      )}

      {/* Stats Row */}
      <div className="px-4 pb-2 flex flex-wrap gap-2">
        {/* Messages */}
        <div data-testid="recap-messages" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-muted/40 border border-border/30">
          <MessageCircle className="w-3 h-3 text-primary" />
          <span className="text-[11px] font-semibold">{recap.total_messages}</span>
          <span className="text-[10px] text-muted-foreground">messages</span>
        </div>

        {/* Conversations */}
        {recap.conversations_count > 0 && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-muted/40 border border-border/30">
            <Brain className="w-3 h-3 text-violet-500" />
            <span className="text-[11px] font-semibold">{recap.conversations_count}</span>
            <span className="text-[10px] text-muted-foreground">{recap.conversations_count === 1 ? "chat" : "chats"}</span>
          </div>
        )}

        {/* Mood */}
        {recap.mood_logs_count > 0 && (
          <div data-testid="recap-mood" className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl ${trend.bg} border border-border/30`}>
            <Heart className="w-3 h-3 text-pink-500" />
            <span className="text-[11px] font-semibold">{recap.avg_mood}/10</span>
            <TrendIcon className={`w-3 h-3 ${trend.color}`} />
            <span className="text-[10px] text-muted-foreground">{moodLabel}</span>
          </div>
        )}

        {/* Streak */}
        {recap.streak > 0 && (
          <div data-testid="recap-streak" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-orange-500/10 border border-border/30">
            <Flame className="w-3 h-3 text-orange-500" />
            <span className="text-[11px] font-semibold">{recap.streak}-day streak</span>
          </div>
        )}
      </div>

      {/* Wisdom Figures & Topics Row */}
      {(recap.wisdom_figures?.length > 0 || recap.topics?.length > 0 || recap.emotions?.length > 0) && (
        <div className="px-4 pb-3 flex flex-wrap gap-1.5">
          {/* Wisdom figures */}
          {recap.wisdom_figures?.map(fig => (
            <span key={fig.key} data-testid={`recap-wisdom-${fig.key}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-400/10 text-amber-700 dark:text-amber-300 border border-amber-300/20">
              <Sparkles className="w-2.5 h-2.5" />
              {fig.name}
            </span>
          ))}
          {/* Emotions */}
          {recap.emotions?.slice(0, 4).map(e => (
            <span key={e} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-pink-400/10 text-pink-700 dark:text-pink-300 border border-pink-300/20">
              {e}
            </span>
          ))}
          {/* Topics */}
          {recap.topics?.slice(0, 3).map((t, i) => (
            <span key={i} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-muted/50 text-muted-foreground border border-border/30 truncate max-w-[120px]">
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
